/**
 * React hooks for telemetry instrumentation.
 * All hooks use useCallback/useRef to avoid unnecessary rerenders.
 * Never block rendering or throw.
 */

import { useCallback, useEffect, useRef } from 'react';
import { trackEvent, type TelemetryEventType } from '@/services/telemetry';

// ── useDwellTime ──
// Tracks how long a section is visible + interaction count (scroll/click/touch)

export function useDwellTime(screen: string, field: string) {
  const ref = useRef<HTMLDivElement>(null);
  const entryTime = useRef<number | null>(null);
  const interactions = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onInteract = () => { interactions.current++; };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entryTime.current = Date.now();
          interactions.current = 0;
          trackEvent({ eventType: 'section_entered', screen, field });
          el.addEventListener('scroll', onInteract, { passive: true });
          el.addEventListener('click', onInteract, { passive: true });
          el.addEventListener('touchstart', onInteract, { passive: true });
        } else if (entryTime.current) {
          const dur = Date.now() - entryTime.current;
          trackEvent({
            eventType: 'section_exited', screen, field,
            durationMs: dur,
            interactionCount: interactions.current,
          });
          entryTime.current = null;
          el.removeEventListener('scroll', onInteract);
          el.removeEventListener('click', onInteract);
          el.removeEventListener('touchstart', onInteract);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', onInteract);
      el.removeEventListener('click', onInteract);
      el.removeEventListener('touchstart', onInteract);
    };
  }, [screen, field]);

  return ref;
}

// ── useTrackInput ──
// Wraps an onChange to fire input_changed events

export function useTrackInput<T extends string | number>(
  screen: string,
  field: string,
  onChange: (value: T) => void,
) {
  const prevRef = useRef<string>('');

  return useCallback((value: T) => {
    trackEvent({
      eventType: 'input_changed', screen, field,
      previousValue: prevRef.current,
      newValue: String(value),
    });
    prevRef.current = String(value);
    onChange(value);
  }, [screen, field, onChange]);
}

// ── useTrackOverride ──
// Fires value_overridden with delta percent

export function useTrackOverride(screen: string, field: string) {
  return useCallback((engineValue: number, userValue: number) => {
    const delta = engineValue !== 0
      ? Math.round(((userValue - engineValue) / engineValue) * 100)
      : 0;
    trackEvent({
      eventType: 'value_overridden', screen, field,
      previousValue: String(engineValue),
      newValue: String(userValue),
      meta: { deltaPercent: delta },
    });
  }, [screen, field]);
}

// ── useTrackFlow ──
// Tracks completion progress through a multi-step flow

export function useTrackFlow(screen: string, totalSteps: number) {
  const startTime = useRef(Date.now());
  const step = useRef(0);
  const completed = useRef(false);

  const advanceStep = useCallback(() => {
    step.current++;
  }, []);

  const markComplete = useCallback(() => {
    completed.current = true;
    trackEvent({
      eventType: 'flow_completed', screen,
      durationMs: Date.now() - startTime.current,
      completionPercent: 100,
    });
  }, [screen]);

  const markAbandoned = useCallback((lastField?: string) => {
    if (completed.current) return;
    trackEvent({
      eventType: 'flow_abandoned', screen,
      field: lastField,
      durationMs: Date.now() - startTime.current,
      completionPercent: Math.round((step.current / totalSteps) * 100),
    });
  }, [screen, totalSteps]);

  return { advanceStep, markComplete, markAbandoned };
}

// ── useTrack ──
// Generic event emitter with screen pre-bound

export function useTrack(screen: string) {
  return useCallback((eventType: TelemetryEventType, data?: Partial<{
    field: string; previousValue: string; newValue: string;
    durationMs: number; meta: Record<string, string | number | boolean>;
  }>) => {
    trackEvent({ eventType, screen, ...data });
  }, [screen]);
}
